// Direct-to-R2 upload helper with progress reporting.
//
// We use XMLHttpRequest rather than fetch because fetch has no upload-progress
// event — `xhr.upload.onprogress` is the only way to drive a real percent bar
// in the browser. All presigned PUT uploads (org logo, gallery, KYC docs) go
// through here so the percent UX is identical everywhere.

/** Called with an integer 0–100 as bytes flush to R2. */
export type UploadProgress = (percent: number) => void;

export function putToSignedUrl(
  uploadUrl: string,
  file: Blob,
  contentType: string,
  onProgress?: UploadProgress,
): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", uploadUrl);
    xhr.setRequestHeader("Content-Type", contentType);

    if (onProgress) {
      // Emit 0% immediately so the UI shows a determinate bar from the start.
      onProgress(0);
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) {
          onProgress(Math.round((e.loaded / e.total) * 100));
        }
      };
    }

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress?.(100);
        resolve();
      } else {
        reject(new Error(`Upload failed (${xhr.status})`));
      }
    };
    xhr.onerror = () => reject(new Error("Upload failed (network error)"));
    xhr.onabort = () => reject(new Error("Upload aborted"));

    xhr.send(file);
  });
}

// Combines per-file progress into a single byte-weighted percent across a set
// of uploads (e.g. KYC's registration + tax docs). Returns a per-file callback
// factory: call `track(index)` to get the `onProgress` for that file.
export function makeAggregateProgress(
  sizes: number[],
  onProgress?: UploadProgress,
): (index: number) => UploadProgress | undefined {
  if (!onProgress) return () => undefined;
  const total = sizes.reduce((a, b) => a + b, 0) || 1;
  const loaded = sizes.map(() => 0);
  return (index: number) => (percent: number) => {
    loaded[index] = (sizes[index] ?? 0) * (percent / 100);
    const sum = loaded.reduce((a, b) => a + b, 0);
    onProgress(Math.round((sum / total) * 100));
  };
}
